import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import { PropsWithChildren, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Label, WsSelectProps } from 'wisestep-base';

interface SelectWCreateProps<T> extends Omit<WsSelectProps, 'selectArray'> {
  onCreate: () => void;
  onChange?(value: string | T): void;
  renderValue?: (value: T) => React.ReactNode;
  showAddTo?: boolean;
}

const CREATE_OPTION_VALUE = 'ADD_OPTION';

export const SelectWCreate = <T,>({
  children,
  onCreate,
  name,
  label,
  size = 'small',
  disabled = false,
  placeholder,
  variant = 'outlined',
  showAddTo = true,
  ...props
}: PropsWithChildren<SelectWCreateProps<T>>) => {
  const form = useFormContext();
  const selectedOption = form.watch(name);

  useEffect(() => {
    if (selectedOption === CREATE_OPTION_VALUE) onCreate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  return (
    <FormControl variant={variant} fullWidth size={size} disabled={disabled}>
      {label && <Label>{label}</Label>}
      <Controller
        render={({ field: { onChange, value = props.defaultValue }, fieldState: { error } }) => (
          <>
            {placeholder && <InputLabel id="select-label-id">{placeholder}</InputLabel>}
            <Select
              labelId="select-label-id"
              id="select-label"
              fullWidth
              onChange={(event, child) => {
                onChange(event, child);
                props.onChange?.(event.target.value);
              }}
              value={value}
              label={placeholder}
              disabled={disabled}
              aria-describedby="component-error-text"
              {...(props.renderValue && { renderValue: props.renderValue })}
            >
              {children}
              <MenuItem
                sx={{ color: 'primary.main' }}
                key={CREATE_OPTION_VALUE}
                value={CREATE_OPTION_VALUE}
              >
                {showAddTo && <>+ Add {label ?? placeholder ?? ''}</>}
              </MenuItem>
            </Select>
            {error && <FormHelperText error>{error.message}</FormHelperText>}
          </>
        )}
        control={form.control}
        name={name}
      />
    </FormControl>
  );
};
